import api from "src/shared/api";
import { EQueryTagTypes } from "src/shared/types/api";
import {
  IPermissionCreateBody,
  IPermissionUpdateBody,
  IPermissionResponse,
  IPermissionListResponse,
} from "../types/api/permission";
import {
  IRoleCreateBody,
  IRoleUpdateBody,
  IRoleResponse,
  IRoleListResponse,
  IRoleDeleteResponse,
} from "../types/api/role";
import {
  IUserPermissionsListResponse,
  IUserPermissionsResponse,
  IUserPermissionsSyncRolesBody,
  IUserPermissionsSyncPermissionsBody,
} from "../types/api/user";

export const permissionApi = api.injectEndpoints({
  endpoints: (build) => ({
    //PERMISSIONS
    permissionCreate: build.mutation<
      IPermissionResponse,
      IPermissionCreateBody
    >({
      query: (body) => ({
        url: "/api/rbac/permissions",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: EQueryTagTypes.PERMISSIONS, id: "PERMISSION_LIST" },
      ],
    }),

    permissionList: build.query<IPermissionListResponse, void>({
      query: () => ({
        url: "/api/rbac/permissions",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: EQueryTagTypes.PERMISSIONS as const,
                id: `PERMISSION_${id}`,
              })),
              { type: EQueryTagTypes.PERMISSIONS, id: "PERMISSION_LIST" },
            ]
          : [{ type: EQueryTagTypes.PERMISSIONS, id: "PERMISSION_LIST" }],
    }),

    permissionGet: build.query<IPermissionResponse, number>({
      query: (permissionId) => ({
        url: `/api/rbac/permissions/${permissionId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, permissionId) => [
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `PERMISSION_${permissionId}`,
        },
      ],
    }),

    permissionUpdate: build.mutation<
      IPermissionResponse,
      { permissionId: number; body: IPermissionUpdateBody }
    >({
      query: ({ permissionId, body }) => ({
        url: `/api/rbac/permissions/${permissionId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { permissionId }) => [
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `PERMISSION_${permissionId}`,
        },
        { type: EQueryTagTypes.PERMISSIONS, id: "PERMISSION_LIST" },
        { type: EQueryTagTypes.PERMISSIONS, id: "ROLE_LIST" },
      ],
    }),

    // ROLES

    roleList: build.query<IRoleListResponse, void>({
      query: () => ({
        url: "/api/rbac/roles",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: EQueryTagTypes.PERMISSIONS as const,
                id: `ROLE_${id}`,
              })),
              { type: EQueryTagTypes.PERMISSIONS, id: "ROLE_LIST" },
            ]
          : [{ type: EQueryTagTypes.PERMISSIONS, id: "ROLE_LIST" }],
    }),

    roleCreate: build.mutation<IRoleResponse, IRoleCreateBody>({
      query: (body) => ({
        url: "/api/rbac/roles",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: EQueryTagTypes.PERMISSIONS, id: "ROLE_LIST" }],
    }),

    roleGet: build.query<IRoleResponse, number>({
      query: (roleId) => ({
        url: `/api/rbac/roles/${roleId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, roleId) => [
        { type: EQueryTagTypes.PERMISSIONS, id: `ROLE_${roleId}` },
      ],
    }),

    roleUpdate: build.mutation<
      IRoleResponse,
      { roleId: number; body: IRoleUpdateBody }
    >({
      query: ({ roleId, body }) => ({
        url: `/api/rbac/roles/${roleId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { roleId }) => [
        { type: EQueryTagTypes.PERMISSIONS, id: `ROLE_${roleId}` },
        { type: EQueryTagTypes.PERMISSIONS, id: "ROLE_LIST" },
        { type: EQueryTagTypes.PERMISSIONS, id: "PERMISSION_LIST" },
      ],
    }),

    roleDelete: build.mutation<IRoleDeleteResponse, number>({
      query: (roleId) => ({
        url: `/api/rbac/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, roleId) => [
        { type: EQueryTagTypes.PERMISSIONS, id: `ROLE_${roleId}` },
        { type: EQueryTagTypes.PERMISSIONS, id: "ROLE_LIST" },
      ],
    }),

    // USERS

    userPermissionsList: build.query<IUserPermissionsListResponse, void>({
      query: () => ({
        url: "/api/rbac/users",
        method: "GET",
      }),
      providesTags: [{ type: EQueryTagTypes.PERMISSIONS, id: "USER_LIST" }],
    }),

    userPermissionsGetUserRoles: build.query<IUserPermissionsResponse, number>({
      query: (userId) => ({
        url: `/api/rbac/users/${userId}/roles`,
        method: "GET",
      }),
      providesTags: (_result, _error, userId) => [
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `USER_ROLES_${userId}`,
        },
      ],
    }),

    userPermissionsGetUserPermissions: build.query<
      IUserPermissionsResponse,
      number
    >({
      query: (userId) => ({
        url: `/api/rbac/users/${userId}/permissions`,
        method: "GET",
      }),
      providesTags: (_result, _error, userId) => [
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `USER_PERMISSIONS_${userId}`,
        },
      ],
    }),

    userPermissionsSyncRoles: build.mutation<
      IUserPermissionsResponse,
      { userId: number; body: IUserPermissionsSyncRolesBody }
    >({
      query: ({ userId, body }) => ({
        url: `/api/rbac/users/${userId}/sync-roles`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `USER_ROLES_${userId}`,
        },
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `USER_PERMISSIONS_${userId}`,
        },
        { type: EQueryTagTypes.PERMISSIONS, id: "USER_LIST" },
      ],
    }),

    userPermissionsSyncPermissions: build.mutation<
      IUserPermissionsResponse,
      { userId: number; body: IUserPermissionsSyncPermissionsBody }
    >({
      query: ({ userId, body }) => ({
        url: `/api/rbac/users/${userId}/sync-permissions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `USER_PERMISSIONS_${userId}`,
        },
        {
          type: EQueryTagTypes.PERMISSIONS,
          id: `USER_ROLES_${userId}`,
        },
        { type: EQueryTagTypes.PERMISSIONS, id: "USER_LIST" },
      ],
    }),
  }),
});

export const {
  usePermissionCreateMutation,
  usePermissionListQuery,
  useLazyPermissionGetQuery,
  usePermissionUpdateMutation,
  useRoleListQuery,
  useRoleCreateMutation,
  useLazyRoleGetQuery,
  useRoleUpdateMutation,
  useRoleDeleteMutation,
  useUserPermissionsListQuery,
  useLazyUserPermissionsGetUserRolesQuery,
  useUserPermissionsGetUserPermissionsQuery,
  useUserPermissionsSyncRolesMutation,
  useUserPermissionsSyncPermissionsMutation,
} = permissionApi;
